import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchStaff,
  createStaff,
  updateStaff,
  hardDeleteStaff,
  setStaffActive,
} from "../../store/slices/staffSlice";
import { staffService } from "../../services/staffService";
import { salonService } from "../../services/salonService";
import { subscriptionService } from "../../services/subscriptionService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const GOREV_ONERILERI = [
  "Kuaför",
  "Makyaj Uzmanı",
  "Masaj Terapisti",
  "Manikürcü",
  "Pedikürcü",
  "Cilt Bakım Uzmanı",
  "Lazer Epilasyon Uzmanı",
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const STATUS_BADGE = {
  COMPLETED: { label: "Tamamlandı", cls: "bg-success/15 text-success border-success/20" },
  // Randevu takvimiyle aynı renk dili: Planlandı mavi, Tamamlandı yeşil.
  SCHEDULED: { label: "Planlandı", cls: "bg-blue-500/15 text-blue-500 border-blue-500/20" },
  CANCELLED: { label: "İptal", cls: "bg-error/15 text-error border-error/20" },
};

const emptyForm = {
  fullName: "",
  position: "",
  specializations: [],
  email: "",
  password: "",
  phone: "",
  hireDate: "",
};

export default function StaffPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, isLoading, error } = useSelector((state) => state.staff);
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === "ADMIN";
  const canWrite = user?.role === "MANAGER"; // yalnız salon yöneticisi değiştirir
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [salons, setSalons] = useState([]);
  const [selectedSalonId, setSelectedSalonId] = useState("");
  const [packages, setPackages] = useState([]);

  // Ekle/Düzenle
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [specInput, setSpecInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Detay kartı
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityStaffId, setActivityStaffId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const ACTIVITY_PAGE_SIZE = 20;

  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  useEffect(() => {
    if (isAdmin) {
      salonService.list({ size: 100 }).then((res) => {
        const list = res.data?.data?.content ?? [];
        setSalons(list);
        setSelectedSalonId((prev) => prev || list[0]?.id || "");
      });
      subscriptionService.list().then((res) => setPackages(res.data?.data ?? []));
    } else {
      dispatch(fetchStaff({ size: 200 }));
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedSalonId) {
      dispatch(fetchStaff({ salonId: selectedSalonId, size: 200 }));
    }
  }, [dispatch, isAdmin, selectedSalonId]);

  const refresh = () =>
    dispatch(fetchStaff(isAdmin ? { salonId: selectedSalonId, size: 200 } : { size: 200 }));

  // Pakete bağlı limit (admin, seçili salon)
  const limit = useMemo(() => {
    if (!isAdmin) return null;
    const salon = salons.find((s) => s.id === selectedSalonId);
    const pkg = packages.find((p) => p.name === salon?.packageName);
    return pkg?.maxStaff ?? null;
  }, [isAdmin, salons, selectedSalonId, packages]);
  const activeCount = items.filter((s) => s.active).length;

  // Görev önerileri: mevcut personel görevleri + varsayılanlar
  const gorevSuggestions = useMemo(() => {
    const set = new Set(GOREV_ONERILERI);
    items.forEach((s) => s.position && set.add(s.position));
    return Array.from(set);
  }, [items]);

  // --- ekle/düzenle ---
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSpecInput("");
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      fullName: row.fullName ?? "",
      position: row.position ?? "",
      specializations: row.specializations ?? [],
      email: row.email ?? "",
      password: "",
      phone: row.phone ?? "",
      hireDate: row.hireDate ?? "",
    });
    setSpecInput("");
    setFormError(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const addSpec = () => {
    const v = specInput.trim();
    if (v && !form.specializations.includes(v)) {
      setForm((f) => ({ ...f, specializations: [...f.specializations, v] }));
    }
    setSpecInput("");
  };
  const removeSpec = (s) =>
    setForm((f) => ({ ...f, specializations: f.specializations.filter((x) => x !== s) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!editingId && (!form.email.trim() || !form.password)) {
      setFormError("Personel girişi için e-posta ve şifre zorunludur.");
      return;
    }
    setSaving(true);
    const base = {
      fullName: form.fullName.trim(),
      position: form.position.trim() || null,
      specializations: form.specializations,
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      hireDate: form.hireDate || null,
    };
    try {
      if (editingId) {
        await dispatch(updateStaff({ id: editingId, payload: base })).unwrap();
        showToast("Personel güncellendi.", "success");
      } else {
        await dispatch(
          createStaff({ ...base, password: form.password || null })
        ).unwrap();
        showToast("Personel eklendi.", "success");
      }
      closeModal();
      refresh();
    } catch (err) {
      setFormError(typeof err === "string" ? err : "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  // --- detay ---
  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetail(null);
    setActivity(null);
    setActivityStaffId(row.id);
    setDetailLoading(true);
    setActivityLoading(true);
    try {
      const res = await staffService.get(row.id);
      setDetail(res.data?.data ?? null);
    } catch {
      showToast("Detay yüklenemedi.", "error");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
    // Hareketler ayrı yüklenir (detayı bloklamasın): ilk sayfa (son hareketler)
    staffService
      .activity(row.id, { page: 0, size: ACTIVITY_PAGE_SIZE })
      .then((res) => setActivity(res.data?.data ?? null))
      .catch(() => setActivity(null))
      .finally(() => setActivityLoading(false));
  };

  // Sonraki sayfayı getirip mevcut listeye ekler
  const loadMoreActivity = async () => {
    if (!activity || !activityStaffId || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await staffService.activity(activityStaffId, {
        page: activity.page + 1,
        size: ACTIVITY_PAGE_SIZE,
      });
      const next = res.data?.data;
      if (next) {
        setActivity((prev) => ({
          ...next,
          appointments: [...(prev?.appointments ?? []), ...(next.appointments ?? [])],
        }));
      }
    } catch {
      showToast("Daha fazla hareket yüklenemedi.", "error");
    } finally {
      setLoadingMore(false);
    }
  };

  // --- sil / durum ---
  const triggerHardDelete = (row) => {
    setConfirmModal({
      isOpen: true,
      title: "Personeli Kalıcı Sil",
      message: `${row.fullName} kalıcı olarak silinecek (giriş hesabı dahil). Bu işlem geri alınamaz. Emin misiniz?`,
      type: "danger",
      onConfirm: () =>
        dispatch(hardDeleteStaff(row.id))
          .unwrap()
          .then(() => showToast("Personel kalıcı silindi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız.", "error")),
    });
  };
  const toggleActive = (row) => {
    dispatch(setStaffActive({ id: row.id, active: !row.active }))
      .unwrap()
      .then(() => showToast(row.active ? "Pasife alındı." : "Aktifleştirildi.", "success"))
      .catch((err) => showToast(err || "İşlem başarısız.", "error"));
  };

  // Filtre ekranında önemli alanlar + denetim (kim/ne zaman)
  const columns = [
    {
      key: "fullName",
      header: "Personel",
      type: "string",
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{val}</span>
          <span className="text-[11px] text-on-surface-variant block">
            {row.email || row.phone || "—"}
            {row.userId && (
              <span className="ml-1 text-primary" title="Giriş hesabı var">● giriş</span>
            )}
          </span>
        </div>
      ),
    },
    { key: "position", header: "Görev", type: "string", sortable: true, render: (v) => v || "—" },
    {
      key: "active",
      header: "Durum",
      sortable: true,
      render: (val) => (
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            val
              ? "bg-success/15 text-success border border-success/20"
              : "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40"
          }`}
        >
          {val ? "Aktif" : "Pasif"}
        </span>
      ),
    },
    { key: "createdBy", header: "Ekleyen", type: "string", sortable: true, render: (v) => v || "—" },
    { key: "createdAt", header: "Eklenme", type: "date", sortable: true, render: (v) => fmtDate(v) },
    { key: "updatedAt", header: "Güncelleme", type: "date", sortable: true, render: (v) => fmtDateTime(v) },
  ];

  const renderActions = (s) => (
    <div className="flex items-center justify-end gap-1.5">
      <button
        onClick={() => openDetail(s)}
        title="Detay"
        className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
      >
        <Icon name="visibility" className="!text-[18px]" />
      </button>
      {canWrite && (
        <>
          <button
            onClick={() => openEdit(s)}
            title="Düzenle"
            className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
          >
            <Icon name="edit" className="!text-[18px]" />
          </button>
          <button
            onClick={() => toggleActive(s)}
            title={s.active ? "Pasife Al" : "Aktifleştir"}
            className={`p-1.5 rounded-lg transition ${
              s.active ? "text-tier-gold hover:bg-tier-gold/10" : "text-success hover:bg-success/10"
            }`}
          >
            <Icon name={s.active ? "pause_circle" : "play_circle"} className="!text-[18px]" />
          </button>
          <button
            onClick={() => triggerHardDelete(s)}
            title="Kalıcı Sil"
            className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
          >
            <Icon name="delete_forever" className="!text-[18px]" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="badge" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Personel</h1>
            <p className="text-on-surface-variant text-sm">
              {canWrite
                ? "Personeli ekleyin, düzenleyin ve yönetin."
                : "Personel listesi (salt görüntüleme)."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isAdmin && salons.length > 0 && (
            <select
              value={selectedSalonId}
              onChange={(e) => setSelectedSalonId(e.target.value)}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
            >
              {salons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          {canWrite && (
            <button
              onClick={openCreate}
              className="bg-primary text-white rounded-xl px-4 py-2 font-semibold text-sm hover:opacity-90 transition flex items-center gap-1.5"
            >
              <Icon name="add" className="!text-[18px]" /> Yeni Personel
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="inline-flex items-center gap-2 text-xs rounded-xl px-3 py-2 border bg-primary/5 text-on-surface-variant border-primary/10">
          <Icon name="visibility" className="!text-[16px]" />
          ADMIN salt-görüntüleme modundadır; personel değişikliklerini salon yöneticisi yapar.
          {limit != null && <span> · Aktif {activeCount}/{limit} (paket limiti)</span>}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={items}
          actions={renderActions}
          searchPlaceholder="Personelde ara..."
        />
      )}

      {/* Ekle/Düzenle Modalı */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="badge" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">
                  {editingId ? "Personeli Düzenle" : "Yeni Personel"}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
              {formError && (
                <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                  <Icon name="error" className="!text-[16px]" /> {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Ad Soyad *</label>
                  <input
                    required
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Görev (dinamik)</label>
                  <input
                    list="gorev-list"
                    value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    placeholder="Örn. Lazer Epilasyon Uzmanı"
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                  <datalist id="gorev-list">
                    {gorevSuggestions.map((g) => (
                      <option key={g} value={g} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">İşe Giriş</label>
                  <input
                    type="date"
                    value={form.hireDate}
                    onChange={(e) => setForm({ ...form, hireDate: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">E-posta *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Telefon</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                </div>

                {!editingId && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold mb-1 text-on-surface-variant">
                      Giriş Şifresi *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="En az 8 karakter, 1 büyük harf, 1 rakam"
                      className="w-full border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                    />
                    <p className="text-[11px] text-on-surface-variant mt-1">
                      Personel bu e-posta ve şifre ile giriş yapar; şifresini sonradan profilinden değiştirebilir.
                    </p>
                  </div>
                )}
              </div>

              {/* Dinamik uzmanlıklar */}
              <div>
                <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Ek Uzmanlıklar</label>
                <div className="flex gap-2">
                  <input
                    value={specInput}
                    onChange={(e) => setSpecInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addSpec();
                      }
                    }}
                    placeholder="Yazıp Enter'a basın…"
                    className="flex-1 border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary"
                  />
                  <button type="button" onClick={addSpec} className="px-3 rounded-xl border border-outline-variant text-primary hover:bg-primary/10 transition">
                    <Icon name="add" className="!text-[18px]" />
                  </button>
                </div>
                {form.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.specializations.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                        {s}
                        <button type="button" onClick={() => removeSpec(s)} className="hover:text-error">
                          <Icon name="close" className="!text-[14px]" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs">
                  İptal
                </button>
                <button type="submit" disabled={saving} className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5">
                  {saving ? "Kaydediliyor…" : (<><Icon name="save" className="!text-[16px]" /> Kaydet</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detay Kartı */}
      {detailOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="badge" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">Personel Detay Kartı</h3>
              </div>
              <button onClick={() => setDetailOpen(false)} className="p-1 rounded-full text-on-surface-variant hover:text-primary transition">
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-5 flex-1 text-sm">
              {detailLoading || !detail ? (
                <div className="py-8 text-center text-on-surface-variant">Yükleniyor…</div>
              ) : (
                <>
                  <div className="flex justify-between items-start bg-primary/5 border border-primary/10 rounded-2xl p-4">
                    <div>
                      <h4 className="text-xl font-bold text-primary">{detail.fullName}</h4>
                      <p className="text-xs text-on-surface-variant mt-0.5">{detail.position || "Görev belirtilmemiş"}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${detail.active ? "bg-success/15 text-success" : "bg-outline-variant/30 text-on-surface-variant"}`}>
                      {detail.active ? "Aktif" : "Pasif"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-surface-container-low rounded-2xl p-4 space-y-1.5 text-xs text-on-surface">
                      <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1">İletişim & Giriş</h5>
                      <p><strong>E-posta:</strong> {detail.email || "—"}</p>
                      <p><strong>Telefon:</strong> {detail.phone || "—"}</p>
                      <p><strong>Giriş hesabı:</strong> {detail.userId ? "Var (personel giriş yapabilir)" : "Yok"}</p>
                    </div>
                    <div className="bg-surface-container-low rounded-2xl p-4 space-y-1.5 text-xs text-on-surface">
                      <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1">İş Bilgileri</h5>
                      <p><strong>İşe Giriş:</strong> {fmtDate(detail.hireDate)}</p>
                      <p><strong>Prim Oranı:</strong> {detail.commissionRate != null ? `%${detail.commissionRate}` : "—"}</p>
                      <p><strong>Saatlik Ücret:</strong> {detail.hourlyRate != null ? `${detail.hourlyRate} ₺` : "—"}</p>
                    </div>
                  </div>

                  <div className="bg-surface-container-low rounded-2xl p-4">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant mb-2">Uzmanlıklar</h5>
                    {detail.specializations?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {detail.specializations.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">{s}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-on-surface-variant">—</p>
                    )}
                  </div>

                  {/* Denetim */}
                  <div className="bg-surface-container-low rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-on-surface">
                    <h5 className="font-bold uppercase tracking-wider text-on-surface-variant mb-1 sm:col-span-2 flex items-center gap-1">
                      <Icon name="history" className="!text-[16px] text-primary" /> Denetim
                    </h5>
                    <p><strong>Ekleyen:</strong> {detail.createdBy || "—"}</p>
                    <p><strong>Eklenme:</strong> {fmtDateTime(detail.createdAt)}</p>
                    <p><strong>Son Güncelleyen:</strong> {detail.updatedBy || "—"}</p>
                    <p><strong>Son Güncelleme:</strong> {fmtDateTime(detail.updatedAt)}</p>
                  </div>

                  {/* Hareketler (randevular / hizmetler) */}
                  <div>
                    <h5 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant mb-2 flex items-center gap-1">
                      <Icon name="event_note" className="!text-[16px] text-primary" /> Son Hareketler
                    </h5>

                    {activityLoading ? (
                      <div className="py-6 text-center text-xs text-on-surface-variant">Hareketler yükleniyor…</div>
                    ) : !activity || activity.totalAppointments === 0 ? (
                      <p className="text-xs text-on-surface-variant bg-surface-container-low rounded-2xl p-4">
                        Bu personelin randevu hareketi yok.
                      </p>
                    ) : (
                      <>
                        {/* Özet kartları */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                          {[
                            { label: "Toplam", value: activity.totalAppointments, cls: "text-on-surface" },
                            { label: "Tamamlanan", value: activity.completed, cls: "text-success" },
                            { label: "Planlı", value: activity.scheduled, cls: "text-primary" },
                            { label: "İptal", value: activity.cancelled, cls: "text-error" },
                          ].map((s) => (
                            <div key={s.label} className="bg-surface-container-low rounded-xl p-3 text-center">
                              <p className={`text-lg font-bold ${s.cls}`}>{s.value}</p>
                              <p className="text-[11px] text-on-surface-variant">{s.label}</p>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-success/5 border border-success/15 rounded-xl p-3">
                            <p className="text-[11px] text-on-surface-variant">Ciro (tamamlanan)</p>
                            <p className="text-base font-bold text-success">{fmtTL(activity.completedRevenue)}</p>
                          </div>
                          <div className="bg-primary/5 border border-primary/15 rounded-xl p-3">
                            <p className="text-[11px] text-on-surface-variant">Tahmini Prim</p>
                            <p className="text-base font-bold text-primary">{fmtTL(activity.estimatedCommission)}</p>
                          </div>
                        </div>

                        {/* Randevu listesi */}
                        <div className="border border-outline-variant rounded-2xl overflow-hidden max-h-72 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-surface-container-low text-on-surface-variant uppercase tracking-wider sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2 font-semibold">Tarih</th>
                                <th className="text-left px-3 py-2 font-semibold">Müşteri</th>
                                <th className="text-left px-3 py-2 font-semibold">Hizmetler</th>
                                <th className="text-right px-3 py-2 font-semibold">Tutar</th>
                                <th className="text-center px-3 py-2 font-semibold">Durum</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-outline-variant">
                              {activity.appointments.map((a) => {
                                const badge = STATUS_BADGE[a.status] ?? STATUS_BADGE.SCHEDULED;
                                return (
                                  <tr key={a.id} className="hover:bg-surface-container-low/50">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      {fmtDate(a.appointmentDate)}
                                      <span className="text-on-surface-variant"> {a.startTime?.slice(0, 5)}</span>
                                    </td>
                                    <td className="px-3 py-2 font-medium text-on-surface">{a.customerName}</td>
                                    <td className="px-3 py-2 text-on-surface-variant">
                                      {a.services?.map((s) => s.name).join(", ") || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold">{fmtTL(a.totalPrice)}</td>
                                    <td className="px-3 py-2 text-center">
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.cls}`}>
                                        {badge.label}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Sayfalama: son hareketler + devamını yükle */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[11px] text-on-surface-variant">
                            {activity.appointments.length} / {activity.totalAppointments} hareket gösteriliyor
                          </span>
                          {activity.hasMore && (
                            <button
                              onClick={loadMoreActivity}
                              disabled={loadingMore}
                              className="text-xs font-semibold text-primary hover:bg-primary/10 rounded-lg px-3 py-1.5 transition disabled:opacity-50 flex items-center gap-1"
                            >
                              <Icon name="expand_more" className="!text-[16px]" />
                              {loadingMore ? "Yükleniyor…" : "Daha Fazla Yükle"}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
